import { useState, useEffect } from 'react'
import { useEmpresa } from '../contexts/EmpresaContext'
import api from '../services/api'
import PlanoContasSelect from '../components/PlanoContasSelect'
import LookupSelect from '../components/LookupSelect'
import ItemSelect from '../components/ItemSelect'

function parseNumBr(v: string) {
  const s = String(v ?? '').trim()
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return Number(s) || 0
}
function fmtQtd(v: string) { return parseNumBr(v).toLocaleString('pt-BR', { minimumFractionDigits: 3 }) }
function fmtMoeda(v: string) { return parseNumBr(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }
function fmtDataBr(iso: string) { return iso ? iso.slice(0, 10).split('-').reverse().join('/') : '-' }

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR',
  'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']

const thItens: React.CSSProperties = {
  padding: '9px 10px', textAlign: 'center', color: '#fff', fontWeight: 700,
  fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
}
const tdItens: React.CSSProperties = { padding: '8px 10px', color: '#374151' }

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
      borderRadius: '0.5rem', padding: '0.7rem 0.9rem', marginBottom: '0.7rem',
    }}>
      <h4 style={{ fontSize: '0.68rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
        {titulo}
      </h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'flex-end' }}>
        {children}
      </div>
    </div>
  )
}

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width, gap: '0.15rem' }}>
      <label style={{ fontSize: '0.66rem' }}>{label}</label>
      {children}
    </div>
  )
}

const SITUACAO_LABELS: Record<string, { texto: string; cor: string; fundo: string }> = {
  '00': { texto: 'Aberto', cor: '#991b1b', fundo: '#fee2e2' },
  CA: { texto: 'Cancelado', cor: '#6b7280', fundo: '#f3f4f6' },
  OK: { texto: 'Atendido', cor: '#15803d', fundo: '#dcfce7' },
}

const SITUACAO_ITEM_LABELS: Record<string, string> = {
  '': 'Aberto', C: 'Cancelado', A: 'Atendido', B: 'Baixado', P: 'Parcial',
}
const SITUACAO_ITEM_CORES: Record<string, { cor: string; fundo: string }> = {
  '': { cor: '#991b1b', fundo: '#fee2e2' },
  C: { cor: '#6b7280', fundo: '#f3f4f6' },
  A: { cor: '#15803d', fundo: '#dcfce7' },
  B: { cor: '#7c2d12', fundo: '#ffedd5' },
  P: { cor: '#92400e', fundo: '#fef3c7' },
}

function CampoBusca({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div style={{ width }}>
      <label style={{ display: 'block', fontSize: '0.66rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.2rem' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

interface ParcelaPagamento {
  parcela: number
  data: string
  valor: string
}

interface ItemOrdem {
  linha: number
  itemCod: string
  itemDes: string
  unidade: string
  complemento1: string
  complemento2: string
  referenciaCod: string
  quantidade: string
  saldo: string
  precoUnitario: string
  descontoPerc: string
  descontoVal: string
  acrescimoPerc: string
  prazoEntrega: string
  valor: string
  cotacao: string
  ipiPerc: string
  ipiValor: string
  destCompraCod: string
  destCompraDes: string
  veiculoCod: string
  veiculoDes: string
  solEstab: string
  solNumero: string
  solLinha: string
}

export default function OrdemCompra() {
  const { empresas, empresaCod, setEmpresaCod, empresaAtual } = useEmpresa()
  const [etapa, setEtapa] = useState<'capa' | 'itens'>('capa')

  function hojeISO() { return new Date().toISOString().slice(0, 10) }
  function parseNum(v: string) { return parseNumBr(v) }
  function fmt(n: number, casas = 2) { return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }) }

  const [pa5ultord, setPa5ultord] = useState<number | null>(null)
  useEffect(() => {
    if (!empresaCod) { setPa5ultord(null); return }
    api.get(`/erp/parcom/${empresaCod}`).then(r => setPa5ultord(r.data?.pa5ultord ?? null)).catch(() => setPa5ultord(null))
  }, [empresaCod])
  const proximoNumero = pa5ultord !== null ? pa5ultord + 1 : null

  const [numeroInput, setNumeroInput] = useState('')
  useEffect(() => {
    if (!numeroInput && proximoNumero) setNumeroInput(String(proximoNumero))
  }, [proximoNumero])
  const numeroExcedeLimite = proximoNumero !== null && numeroInput !== '' && Number(numeroInput) > proximoNumero

  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')
  const [fornecedorBloqueado, setFornecedorBloqueado] = useState(false)

  const [compradorCod, setCompradorCod] = useState('')
  const [compradorDes, setCompradorDes] = useState('')
  const [ctaAdministrativaCod, setCtaAdministrativaCod] = useState('')
  const [, setCtaAdministrativaDes] = useState('')

  const [indicadorFrete, setIndicadorFrete] = useState('9')
  const [valorFrete, setValorFrete] = useState('0,00')

  const [descontoCod, setDescontoCod] = useState('')
  const [descontoPerc, setDescontoPerc] = useState('0,000')
  const [valorDesconto, setValorDesconto] = useState('0,00')
  const [geralDif, setGeralDif] = useState('G')

  const [condicaoPgtoCod, setCondicaoPgtoCod] = useState('')
  const [condicaoPgtoDes, setCondicaoPgtoDes] = useState('')

  const [tipoCobranca, setTipoCobranca] = useState('B')
  const [dataEmissao, setDataEmissao] = useState(hojeISO)
  const [observacao, setObservacao] = useState('')

  const [enderecoEntrega, setEnderecoEntrega] = useState('')
  const [cepEntrega, setCepEntrega] = useState('')
  const [cidadeEntrega, setCidadeEntrega] = useState('')
  const [ufEntrega, setUfEntrega] = useState('')
  const [modalEntregaAberto, setModalEntregaAberto] = useState(false)

  const [comentario, setComentario] = useState('')
  const [modalComentarioAberto, setModalComentarioAberto] = useState(false)

  const [parcelas, setParcelas] = useState<ParcelaPagamento[]>([])
  const [modalPagamentoAberto, setModalPagamentoAberto] = useState(false)

  function adicionarParcela() {
    setParcelas(prev => [...prev, { parcela: prev.length + 1, data: '', valor: '0,00' }])
  }
  function removerParcela(parcela: number) {
    setParcelas(prev => prev.filter(p => p.parcela !== parcela).map((p, i) => ({ ...p, parcela: i + 1 })))
  }
  function alterarParcela(parcela: number, campo: 'data' | 'valor', valor: string) {
    setParcelas(prev => prev.map(p => p.parcela === parcela ? { ...p, [campo]: valor } : p))
  }

  const [situacaoCod, setSituacaoCod] = useState('00')
  const [usuarioInclusao, setUsuarioInclusao] = useState('')
  const [dataInclusao, setDataInclusao] = useState('')
  const situacao = SITUACAO_LABELS[situacaoCod]

  const [modoEdicao, setModoEdicao] = useState(false)
  const [itensOrdem, setItensOrdem] = useState<ItemOrdem[]>([])

  const [salvando, setSalvando] = useState(false)
  const [erroSalvar, setErroSalvar] = useState<string | null>(null)
  const [ordemSalva, setOrdemSalva] = useState(false)

  async function salvarOrdem() {
    setSalvando(true); setErroSalvar(null)
    try {
      await api.post('/erp/ordem-compra', {
        empcod: empresaCod, numero: Number(numeroInput),
        fornecedorCod, compradorCod, ctaAdministrativaCod,
        indicadorFrete, valorFrete, descontoCod, valorDesconto, geralDif,
        condicaoPgtoCod, tipoCobranca, dataEmissao, observacao,
        enderecoEntrega, cepEntrega, cidadeEntrega, ufEntrega, comentario,
        parcelas, itens: itensOrdem,
      })
      setOrdemSalva(true)
      setModoEdicao(true)
    } catch (e: any) {
      setErroSalvar(e?.response?.data?.error || 'Erro ao salvar a ordem')
    } finally {
      setSalvando(false)
    }
  }

  function limparFormulario() {
    setFornecedorCod(''); setFornecedorDes(''); setFornecedorBloqueado(false); setCompradorCod(''); setCompradorDes('')
    setCtaAdministrativaCod(''); setCtaAdministrativaDes('')
    setIndicadorFrete('9'); setValorFrete('0,00')
    setDescontoCod(''); setDescontoPerc('0,000'); setValorDesconto('0,00'); setGeralDif('G')
    setCondicaoPgtoCod(''); setCondicaoPgtoDes('')
    setTipoCobranca('B'); setDataEmissao(hojeISO()); setObservacao('')
    setEnderecoEntrega(''); setCepEntrega('')
    setCidadeEntrega(''); setUfEntrega('')
    setComentario('')
    setParcelas([])
    setSituacaoCod('00'); setUsuarioInclusao(''); setDataInclusao('')
    setItensOrdem([])
    setOrdemSalva(false); setErroSalvar(null)
  }

  useEffect(() => {
    if (!empresaCod || !numeroInput || !/^\d+$/.test(numeroInput)) return
    if (proximoNumero !== null && Number(numeroInput) >= proximoNumero) {
      if (modoEdicao) { limparFormulario(); setModoEdicao(false) }
      return
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/erp/ordem-compra/${empresaCod}/${numeroInput}`)
        const d = r.data
        setFornecedorCod(d.fornecedorCod); setFornecedorDes(d.fornecedorDes)
        setCompradorCod(d.compradorCod); setCompradorDes(d.compradorDes)
        setCtaAdministrativaCod(d.ctaAdministrativaCod); setCtaAdministrativaDes(d.ctaAdministrativaDes)
        setIndicadorFrete(d.indicadorFrete); setValorFrete(String(d.valorFrete))
        setDescontoCod(d.descontoCod); setValorDesconto(String(d.valorDesconto)); setGeralDif(d.geralDif)
        setCondicaoPgtoCod(d.condicaoPgtoCod); setCondicaoPgtoDes(d.condicaoPgtoDes)
        setTipoCobranca(d.tipoCobranca); setDataEmissao(d.dataEmissao || hojeISO()); setObservacao(d.observacao)
        setEnderecoEntrega(d.enderecoEntrega || ''); setCepEntrega(d.cepEntrega || '')
        setCidadeEntrega(d.cidadeEntrega || ''); setUfEntrega(d.ufEntrega || '')
        setComentario(d.comentario || '')
        setParcelas((d.parcelas || []).map((p: any) => ({ parcela: p.parcela, data: p.data, valor: fmt(Number(p.valor) || 0) })))
        setSituacaoCod(d.situacaoCod); setUsuarioInclusao(d.usuarioInclusao); setDataInclusao(d.dataInclusao)
        const itensCarregados: ItemOrdem[] = (d.itens || []).map((it: any) => ({
          linha: it.linha, itemCod: it.itemCod, itemDes: it.itemDes, unidade: it.unidade,
          complemento1: it.complemento1, complemento2: it.complemento2,
          referenciaCod: it.referenciaCod,
          quantidade: it.quantidade, saldo: it.saldo, precoUnitario: it.precoUnitario,
          descontoPerc: it.descontoPerc, descontoVal: it.descontoVal, acrescimoPerc: it.acrescimoPerc,
          prazoEntrega: it.prazoEntrega, valor: it.valor, cotacao: it.cotacao,
          ipiPerc: it.ipiPerc, ipiValor: it.ipiValor,
          destCompraCod: it.destCompraCod, destCompraDes: it.destCompraDes,
          veiculoCod: it.veiculoCod, veiculoDes: '',
          solEstab: it.solEstab !== null && it.solEstab !== undefined ? String(it.solEstab) : '',
          solNumero: it.solNumero !== null && it.solNumero !== undefined ? String(it.solNumero) : '',
          solLinha: it.solLinha !== null && it.solLinha !== undefined ? String(it.solLinha) : '',
        }))
        setItensOrdem(itensCarregados)
        const primeiro = itensCarregados[0]
        if (primeiro) {
          setItemCod(primeiro.itemCod); setItemDes(primeiro.itemDes); setUnidadeItem(primeiro.unidade)
          setComplemento1(primeiro.complemento1); setComplemento2(primeiro.complemento2)
          setReferenciaCod(primeiro.referenciaCod)
          setQuantidadeItem(primeiro.quantidade); setSaldoItem(primeiro.saldo)
          setPrecoUnitario(primeiro.precoUnitario); setItemDescontoPerc(primeiro.descontoPerc)
          setItemDescontoVal(primeiro.descontoVal); setItemAcrescimoPerc(primeiro.acrescimoPerc)
          setPrazoEntregaItem(primeiro.prazoEntrega); setValorItem(primeiro.valor); setCotacao(primeiro.cotacao)
          setIpiPerc(primeiro.ipiPerc); setIpiValor(primeiro.ipiValor)
          setDestCompraCod(primeiro.destCompraCod); setDestCompraDes(primeiro.destCompraDes)
          setDestVeiObrigatorio(false)
          setVeiculoCod(primeiro.veiculoCod); setVeiculoDes(primeiro.veiculoDes)
          setSolEstab(primeiro.solEstab); setSolNumero(primeiro.solNumero); setSolLinha(primeiro.solLinha)
          setLinhaAtual(1)
        } else {
          limparFormularioItem(1)
        }
        setModoEdicao(true)
      } catch {
        if (modoEdicao) { limparFormulario(); setModoEdicao(false) }
      }
    }, 500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaCod, numeroInput, proximoNumero])

  const [solEstab, setSolEstab] = useState('')
  const [solNumero, setSolNumero] = useState('')
  const [solLinha, setSolLinha] = useState('')
  const [buscandoSolicitacao, setBuscandoSolicitacao] = useState(false)
  const [erroSolicitacao, setErroSolicitacao] = useState<string | null>(null)

  const [linhaAtual, setLinhaAtual] = useState(1)

  const [itemCod, setItemCod] = useState('')
  const [itemDes, setItemDes] = useState('')
  const [unidadeItem, setUnidadeItem] = useState('')
  const [complemento1, setComplemento1] = useState('')
  const [complemento2, setComplemento2] = useState('')
  const [referenciaCod, setReferenciaCod] = useState('')
  const [quantidadeItem, setQuantidadeItem] = useState('')
  const [quantidadeDestacada, setQuantidadeDestacada] = useState(false)
  const [saldoItem, setSaldoItem] = useState('0,000')
  const [precoUnitario, setPrecoUnitario] = useState('0,0000')
  const [itemDescontoPerc, setItemDescontoPerc] = useState('0,000')
  const [itemDescontoVal, setItemDescontoVal] = useState('0,00')
  const [itemAcrescimoPerc, setItemAcrescimoPerc] = useState('0,000')
  const [prazoEntregaItem, setPrazoEntregaItem] = useState(hojeISO)
  const [valorItem, setValorItem] = useState('0,00')
  const [cotacao, setCotacao] = useState('0')
  const [ipiPerc, setIpiPerc] = useState('0,000')
  const [ipiValor, setIpiValor] = useState('0,00')
  const [destCompraCod, setDestCompraCod] = useState('')
  const [destCompraDes, setDestCompraDes] = useState('')
  const [destVeiObrigatorio, setDestVeiObrigatorio] = useState(false)
  const [veiculoCod, setVeiculoCod] = useState('')
  const [veiculoDes, setVeiculoDes] = useState('')

  function limparFormularioItem(linha: number) {
    setItemCod(''); setItemDes(''); setUnidadeItem(''); setComplemento1(''); setComplemento2('')
    setReferenciaCod(''); setQuantidadeItem(''); setQuantidadeDestacada(false); setSaldoItem('0,000')
    setPrecoUnitario('0,0000'); setItemDescontoPerc(descontoPerc); setItemDescontoVal('0,00'); setItemAcrescimoPerc('0,000')
    setPrazoEntregaItem(hojeISO()); setValorItem('0,00'); setCotacao('0'); setIpiPerc('0,000'); setIpiValor('0,00')
    setDestCompraCod(''); setDestCompraDes(''); setDestVeiObrigatorio(false); setVeiculoCod(''); setVeiculoDes('')
    setSolLinha(''); setErroSolicitacao(null)
    setLinhaAtual(linha)
  }

  function selecionarLinha(numero: number) {
    const max = itensOrdem.length + 1
    const alvo = Math.min(Math.max(1, numero), max)
    const existente = itensOrdem.find(it => it.linha === alvo)
    if (existente) {
      setItemCod(existente.itemCod); setItemDes(existente.itemDes); setUnidadeItem(existente.unidade)
      setComplemento1(existente.complemento1); setComplemento2(existente.complemento2)
      setReferenciaCod(existente.referenciaCod)
      setQuantidadeItem(existente.quantidade); setSaldoItem(existente.saldo)
      setPrecoUnitario(existente.precoUnitario); setItemDescontoPerc(existente.descontoPerc)
      setItemDescontoVal(existente.descontoVal); setItemAcrescimoPerc(existente.acrescimoPerc)
      setPrazoEntregaItem(existente.prazoEntrega); setValorItem(existente.valor); setCotacao(existente.cotacao)
      setIpiPerc(existente.ipiPerc); setIpiValor(existente.ipiValor)
      setDestCompraCod(existente.destCompraCod); setDestCompraDes(existente.destCompraDes)
      setDestVeiObrigatorio(false)
      setVeiculoCod(existente.veiculoCod); setVeiculoDes(existente.veiculoDes)
      setSolEstab(existente.solEstab); setSolNumero(existente.solNumero); setSolLinha(existente.solLinha)
      setLinhaAtual(alvo)
    } else {
      limparFormularioItem(alvo)
    }
  }

  function adicionarItem() {
    const item: ItemOrdem = {
      linha: linhaAtual, itemCod, itemDes, unidade: unidadeItem, complemento1, complemento2,
      referenciaCod, quantidade: quantidadeItem, saldo: saldoItem, precoUnitario,
      descontoPerc: itemDescontoPerc, descontoVal: itemDescontoVal, acrescimoPerc: itemAcrescimoPerc,
      prazoEntrega: prazoEntregaItem, valor: valorItem, cotacao, ipiPerc, ipiValor,
      destCompraCod, destCompraDes, veiculoCod, veiculoDes,
      solEstab, solNumero, solLinha,
    }
    const jaExistia = itensOrdem.some(it => it.linha === linhaAtual)
    setItensOrdem(prev => jaExistia ? prev.map(it => it.linha === linhaAtual ? item : it) : [...prev, item])
    const novoTotalLinhas = jaExistia ? itensOrdem.length : itensOrdem.length + 1
    limparFormularioItem(novoTotalLinhas + 1)
  }

  function removerItem(linha: number) {
    setItensOrdem(prev => prev.filter(it => it.linha !== linha).map((it, i) => ({ ...it, linha: i + 1 })))
    limparFormularioItem(itensOrdem.length)
  }

  const itemEhNovo = !itensOrdem.some(it => it.linha === linhaAtual)

  // Saldo em estoque do item (frota.stqscc), só informativo pro comprador — não é o
  // Ppc_qtdsal (esse continua sendo salvo igual à quantidade comprada, como manda o ERP).
  useEffect(() => {
    if (!itemCod) { setSaldoItem('0,000'); return }
    api.get(`/erp/itens/${itemCod}/saldo`)
      .then(r => setSaldoItem(fmt(Number(r.data?.saldoQtd ?? 0), 3)))
      .catch(() => setSaldoItem('0,000'))
  }, [itemCod])

  // Preço sugerido pela última compra do item, só ao incluir item novo
  useEffect(() => {
    if (!itemCod || !itemEhNovo) return
    api.get(`/erp/itens/${itemCod}/ultima-compra`).then(r => {
      if (r.data?.valorUnitario) setPrecoUnitario(fmt(Number(r.data.valorUnitario), 4))
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCod])

  // Ao trocar de item (só em item novo), a referência escolhida não vale mais
  useEffect(() => {
    if (itemEhNovo) setReferenciaCod('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCod])

  // Busca automática do item pela Solicitação de Compra (Estab/Número/Linha) —
  // só em item novo, senão sobrescreveria os dados de uma linha já adicionada.
  useEffect(() => {
    if (!itemEhNovo) return
    if (!solEstab || !solNumero || !solLinha) { setErroSolicitacao(null); return }
    let cancelado = false
    const t = setTimeout(async () => {
      setBuscandoSolicitacao(true); setErroSolicitacao(null)
      try {
        const r = await api.get(`/erp/solicitacao-compra/${solEstab}/${solNumero}`)
        if (cancelado) return
        const item = (r.data?.itens || []).find((it: any) => String(it.linha) === solLinha)
        if (!item) {
          setErroSolicitacao(`Não encontrei o item ${solLinha} na solicitação ${solNumero}.`)
          return
        }
        if (item.statusLabel !== 'Pendente') {
          setErroSolicitacao(`Item ${solLinha} está "${item.statusLabel}" — não pode ser usado numa nova ordem.`)
          return
        }
        setItemCod(item.itemCod); setItemDes(item.itemDes); setUnidadeItem(item.unidade)
        // type="number" não aceita vírgula decimal
        setQuantidadeItem(String(Number(item.quantidade) || 0))
        setQuantidadeDestacada(true)
        setDestCompraCod(item.destCompraCod); setDestCompraDes(item.destCompraDes || '')
        setPrazoEntregaItem(item.dataEntrega ? String(item.dataEntrega).slice(0, 10) : hojeISO())

        if (item.destCompraCod) {
          try {
            const d = await api.get('/erp/oedcad/lista', { params: { busca: item.destCompraCod } })
            const destino = (d.data || []).find((o: any) => o.oed_cod === item.destCompraCod)
            if (!cancelado) setDestVeiObrigatorio(destino?.oed_vei === 'S')
          } catch { /* segue sem travar */ }
        }
      } catch {
        if (!cancelado) setErroSolicitacao(`Solicitação ${solNumero} não encontrada no estabelecimento ${solEstab}.`)
      } finally {
        if (!cancelado) setBuscandoSolicitacao(false)
      }
    }, 400)
    return () => { cancelado = true; clearTimeout(t) }
  }, [solEstab, solNumero, solLinha, itemEhNovo])

  // Valor Desconto / Valor IPI / Valor calculados igual às fórmulas da TORDITE
  useEffect(() => {
    const qtd = parseNum(quantidadeItem)
    const preco = parseNum(precoUnitario)
    const percDesc = parseNum(itemDescontoPerc)
    const percIpi = parseNum(ipiPerc)

    let valDesc: number
    if (percDesc > 0) {
      valDesc = (preco * qtd) * percDesc / 100
      setItemDescontoVal(fmt(valDesc))
    } else if (geralDif === 'G') {
      valDesc = 0
      setItemDescontoVal(fmt(0))
    } else {
      valDesc = parseNum(itemDescontoVal)
    }

    const valIpi = ((preco * qtd) - valDesc) * percIpi / 100
    setIpiValor(fmt(valIpi))
    setValorItem(fmt((qtd * preco) - valDesc + valIpi))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantidadeItem, precoUnitario, itemDescontoPerc, ipiPerc, geralDif])

  const valorDescontoTravado = parseNum(itemDescontoPerc) > 0 || geralDif === 'G'

  const quantTotal = itensOrdem.reduce((s, it) => s + (Number(it.quantidade) || 0), 0)
  const valorTotal = itensOrdem.reduce((s, it) => s + parseNumBr(it.valor), 0)

  return (
    <div style={{ maxWidth: etapa === 'itens' ? 1040 : 720 }}>
      {etapa === 'capa' && (
      <div className="card" style={{ padding: '1.1rem 1.3rem' }}>
        <h3 style={{
          fontSize: '0.98rem', fontWeight: 700, marginBottom: '0.8rem', color: '#1a6ea8',
          paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        }}>
          Ordem de Compra
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TORDCOM</span>
        </h3>

        <Secao titulo="Cabeçalho">
          <Campo label="Estab." width={55}>
            <select value={empresaCod ?? ''} onChange={e => setEmpresaCod(Number(e.target.value))}>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod}</option>)}
            </select>
          </Campo>
          <Campo label="Nome do Estabelecimento" width={190}>
            <input type="text" value={empresaAtual?.empfan || empresaAtual?.empnom || ''} readOnly disabled />
          </Campo>
          <Campo label="Número" width={90}>
            <input type="text" inputMode="numeric" value={numeroInput}
              onChange={e => setNumeroInput(e.target.value.replace(/\D/g, ''))}
              style={{ fontWeight: 700, color: numeroExcedeLimite ? '#dc2626' : '#1a6ea8', borderColor: numeroExcedeLimite ? '#dc2626' : undefined }} />
          </Campo>
          <Campo label="Tipo" width={150}>
            <select disabled value="C">
              <option value="C">Ordem Compra</option>
            </select>
          </Campo>
          {modoEdicao && (
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '0.3rem 0.6rem', borderRadius: 6 }}>
              Editando ordem nº {numeroInput} já existente
            </span>
          )}

          <CampoBusca label="Fornecedor" width={300}>
            <LookupSelect
              endpoint="/erp/clientes/lista"
              codKey="cli_cod" descKey="cli_fan"
              renderItem={(item: any) => (
                <>
                  <div className="nat-item-linha1">
                    <span className="nat-item-cod">{item.cli_mat}</span>
                    {item.bloqueado && (
                      <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: '#dc2626', borderRadius: 4, padding: '1px 6px' }}>
                        BLOQUEADO
                      </span>
                    )}
                  </div>
                  <div className="nat-item-dsc">{item.cli_fan || item.cli_nom}</div>
                </>
              )}
              buildDesc={(item: any) => item.cli_fan || item.cli_nom}
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des, item) => { setFornecedorCod(cod); setFornecedorDes(des); setFornecedorBloqueado(!!item?.bloqueado) }}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </CampoBusca>
          {fornecedorBloqueado && (
            <div style={{
              width: '100%', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
              borderRadius: 6, padding: '0.5rem 0.8rem', fontSize: '0.8rem', fontWeight: 600,
            }}>
              ⚠ Este fornecedor está BLOQUEADO — não é possível continuar a Ordem de Compra.
            </div>
          )}
          <CampoBusca label="Comprador" width={220}>
            <LookupSelect
              endpoint="/compradores"
              codKey="cpr_cod" descKey="cpr_des"
              codValue={compradorCod} descValue={compradorDes}
              onSelect={(cod, des) => { setCompradorCod(cod); setCompradorDes(des) }}
              placeholder="Buscar comprador..." listWidth={260}
            />
          </CampoBusca>
          <Campo label="Cta Administração" width={220}>
            <PlanoContasSelect empcod={empresaCod} value={ctaAdministrativaCod}
              onChange={(cod, des) => { setCtaAdministrativaCod(cod); setCtaAdministrativaDes(des) }} />
          </Campo>
        </Secao>

        <Secao titulo="Frete e Desconto">
          <Campo label="Indicador Frete" width={220}>
            <select value={indicadorFrete} onChange={e => setIndicadorFrete(e.target.value)}>
              <option value="9">Sem Cobrança de Frete</option>
              <option value="1">Pago pelo Dest./Remetente</option>
              <option value="0">Pago pelo Emitente</option>
              <option value="2">Pago por Terceiros</option>
            </select>
          </Campo>
          <Campo label="Valor Frete" width={110}>
            <input type="text" inputMode="decimal" value={valorFrete} disabled={indicadorFrete === '9'}
              onChange={e => setValorFrete(e.target.value)} />
          </Campo>
          <Campo label="Desconto" width={90}>
            <input type="text" inputMode="numeric" maxLength={2} value={descontoCod} onChange={e => setDescontoCod(e.target.value.replace(/\D/g, '').slice(0, 2))} />
          </Campo>
          <Campo label="Desconto %" width={90}>
            <input type="text" inputMode="decimal" value={descontoPerc} onChange={e => setDescontoPerc(e.target.value)} />
          </Campo>
          <Campo label="Valor Desconto" width={110}>
            <input type="text" inputMode="decimal" value={valorDesconto} onChange={e => setValorDesconto(e.target.value)} />
          </Campo>
          <Campo label="Geral/Dif" width={70}>
            <select value={geralDif} onChange={e => setGeralDif(e.target.value)}>
              <option value="G">G</option>
              <option value="D">D</option>
            </select>
          </Campo>
        </Secao>

        <Secao titulo="Pagamento">
          <CampoBusca label="Condição Pgto" width={260}>
            <LookupSelect
              endpoint="/condicao-pagamento"
              codKey="tcv_cod" descKey="tcv_des"
              codValue={condicaoPgtoCod} descValue={condicaoPgtoDes}
              onSelect={(cod, des) => { setCondicaoPgtoCod(cod); setCondicaoPgtoDes(des) }}
              placeholder="Buscar condição de pagamento..." listWidth={300}
            />
          </CampoBusca>
          <Campo label="Tipo Cobrança" width={200}>
            <select value={tipoCobranca} onChange={e => setTipoCobranca(e.target.value)}>
              <option value="B">Boleto Bancário</option>
              <option value="R">Cartão de Crédito</option>
              <option value="F">Cobrança Bancária</option>
              <option value="P">Pix</option>
              <option value="Q">Cheque</option>
              <option value="D">Depósito Conta</option>
              <option value="H">Dinheiro</option>
              <option value="C">Em Carteira</option>
            </select>
          </Campo>
          <Campo label="Data Emissão" width={140}>
            <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} />
          </Campo>
        </Secao>

        <div style={{ marginBottom: '0.7rem', display: 'flex', gap: '0.5rem' }}>
          <button type="button" className="btn btn-secondary" onClick={() => setModalEntregaAberto(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            📍 Dados de Entrega
            {enderecoEntrega && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, color: '#15803d', background: '#dcfce7',
                borderRadius: 4, padding: '2px 6px',
              }}>
                preenchido
              </span>
            )}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setModalComentarioAberto(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            💬 Comentários
            {comentario && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, color: '#15803d', background: '#dcfce7',
                borderRadius: 4, padding: '2px 6px',
              }}>
                preenchido
              </span>
            )}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setModalPagamentoAberto(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            💰 Pagamentos
            {parcelas.length > 0 && (
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, color: '#15803d', background: '#dcfce7',
                borderRadius: 4, padding: '2px 6px',
              }}>
                {parcelas.length} parcela(s)
              </span>
            )}
          </button>
        </div>

        <Secao titulo="Observação">
          <input type="text" maxLength={40} style={{ width: 40 * 9 + 30 }} value={observacao} onChange={e => setObservacao(e.target.value)} />
        </Secao>

        <Secao titulo="Situação">
          <Campo label="Situação" width={55}>
            <input type="text" value={situacaoCod} readOnly disabled />
          </Campo>
          {situacao && (
            <span style={{
              fontSize: '0.75rem', fontWeight: 700, padding: '0.4rem 0.7rem', borderRadius: 6,
              background: situacao.fundo, color: situacao.cor, marginBottom: 2,
            }}>
              {situacao.texto}
            </span>
          )}
          <Campo label="Usuário Inclusão" width={140}>
            <input type="text" value={usuarioInclusao} readOnly disabled
              style={{ background: '#fef3c7', color: '#92400e' }} />
          </Campo>
          <Campo label="Data Inclusão" width={140}>
            <input type="date" value={dataInclusao} readOnly disabled
              style={{ background: '#fef3c7', color: '#92400e' }} />
          </Campo>
        </Secao>

        {erroSalvar && <div className="alert alert-danger" style={{ marginTop: '0.9rem' }}>{erroSalvar}</div>}
        {ordemSalva && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #86efac', color: '#15803d',
            borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.9rem',
          }}>
            ✓ Ordem de Compra nº {numeroInput} salva com sucesso.
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '0.9rem' }}>
          <button className="btn btn-primary" onClick={() => setEtapa('itens')} disabled={numeroExcedeLimite || fornecedorBloqueado}>
            Próximo — Itens →
          </button>
          <button className="btn btn-primary" onClick={salvarOrdem}
            disabled={salvando || numeroExcedeLimite || fornecedorBloqueado || itensOrdem.length === 0}
            title={itensOrdem.length === 0 ? 'Adicione ao menos um item antes de salvar' : undefined}>
            {salvando ? 'Salvando...' : 'Salvar Ordem de Compra'}
          </button>
          {modoEdicao && (
            <button className="btn btn-secondary"
              onClick={() => window.open(`/api/erp/ordem-compra/${empresaCod}/${numeroInput}/comprovante`, '_blank')}>
              Imprimir Comprovante
            </button>
          )}
          {numeroExcedeLimite && (
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#dc2626' }}>
              Número inválido — o próximo disponível é {proximoNumero}. Corrija antes de continuar.
            </span>
          )}
        </div>
      </div>
      )}

      {etapa === 'itens' && (
      <div className="card" style={{ padding: '1.1rem 1.3rem' }}>
        <h3 style={{
          fontSize: '0.98rem', fontWeight: 700, marginBottom: '0.8rem', color: '#1a6ea8',
          paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        }}>
          Ordem de Compra — Itens
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TORDITE</span>
        </h3>

        <Secao titulo="Cabeçalho">
          <Campo label="Estab." width={55}>
            <input type="text" value={empresaCod ?? ''} readOnly disabled />
          </Campo>
          <Campo label="Nome do Estabelecimento" width={190}>
            <input type="text" value={empresaAtual?.empfan || empresaAtual?.empnom || ''} readOnly disabled />
          </Campo>
          <Campo label="Ordem" width={90}>
            <input type="text" value={numeroInput} readOnly disabled style={{ fontWeight: 700, color: '#1a6ea8' }} />
          </Campo>
          <Campo label="Linha" width={70}>
            <input type="number" min={1} value={linhaAtual}
              onChange={e => selecionarLinha(Number(e.target.value) || 1)}
              style={{ fontWeight: 700, color: '#1a6ea8' }} />
          </Campo>
        </Secao>

        <Secao titulo="Solicitação de Compra">
          <Campo label="Buscar Por" width={210}>
            <select disabled value="Solicitação">
              <option value="Solicitação">Solicitação de Compra</option>
            </select>
          </Campo>
          <Campo label="Estab" width={60}>
            <input type="text" inputMode="numeric" value={solEstab} onChange={e => setSolEstab(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Número" width={90}>
            <input type="text" inputMode="numeric" value={solNumero} onChange={e => setSolNumero(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Linha" width={60}>
            <input type="text" inputMode="numeric" value={solLinha} onChange={e => setSolLinha(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          {buscandoSolicitacao && (
            <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Buscando item da solicitação...</span>
          )}
          {erroSolicitacao && (
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#dc2626' }}>{erroSolicitacao}</span>
          )}
        </Secao>

        <Secao titulo="Item">
          <CampoBusca label="Item" width={460}>
            <ItemSelect
              codValue={itemCod} descValue={itemDes} unValue={unidadeItem}
              onChange={(cod, des, un) => { setItemCod(cod); setItemDes(des); setUnidadeItem(un) }}
            />
          </CampoBusca>
          <Campo label="Complemento 1" width={300}>
            <input type="text" value={complemento1} onChange={e => setComplemento1(e.target.value)} />
          </Campo>
          <Campo label="Complemento 2" width={300}>
            <input type="text" value={complemento2} onChange={e => setComplemento2(e.target.value)} />
          </Campo>
        </Secao>

        <Secao titulo="Referência e Quantidade">
          <CampoBusca label="Referência" width={200}>
            <LookupSelect
              endpoint={`/erp/itens/${itemCod || 0}/referencias`}
              codKey="its_ref" descKey="its_ref"
              codValue={referenciaCod} descValue={referenciaCod}
              onSelect={cod => setReferenciaCod(cod)}
              placeholder={itemCod ? 'Buscar referência...' : 'Escolha o item primeiro'}
              listWidth={220}
            />
          </CampoBusca>
          <Campo label="Quantidade" width={100}>
            <input type="number" min={0} value={quantidadeItem}
              onChange={e => { setQuantidadeItem(e.target.value); setQuantidadeDestacada(false) }}
              style={{
                color: '#dc2626', fontWeight: 700,
                background: quantidadeDestacada ? '#fef3c7' : undefined,
                borderColor: quantidadeDestacada ? '#f59e0b' : undefined,
              }}
              title={quantidadeDestacada ? 'Veio da Solicitação de Compra — confira antes de salvar' : undefined} />
          </Campo>
          <Campo label="Unidade" width={70}>
            <input type="text" value={unidadeItem} readOnly disabled title="Vem do cadastro do item" />
          </Campo>
          <Campo label="Saldo Estoque" width={100}>
            <input type="text" value={saldoItem} readOnly disabled title="Saldo atual em estoque do item" />
          </Campo>
        </Secao>

        <Secao titulo="Preço e Desconto">
          <Campo label="Preço Unitário" width={110}>
            <input type="text" inputMode="decimal" value={precoUnitario} onChange={e => setPrecoUnitario(e.target.value)}
              style={{ color: '#15803d', fontWeight: 700 }} />
          </Campo>
          <Campo label="Desconto %" width={90}>
            <input type="text" inputMode="decimal" value={itemDescontoPerc} onChange={e => setItemDescontoPerc(e.target.value)} />
          </Campo>
          <Campo label="Val. Desconto" width={100}>
            <input type="text" inputMode="decimal" value={itemDescontoVal} disabled={valorDescontoTravado}
              title={valorDescontoTravado ? 'Calculado automaticamente (tem % de desconto, ou o desconto do cabeçalho é Geral)' : undefined}
              onChange={e => setItemDescontoVal(e.target.value)} />
          </Campo>
          <Campo label="Acréscimo %" width={90}>
            <input type="text" inputMode="decimal" value={itemAcrescimoPerc} onChange={e => setItemAcrescimoPerc(e.target.value)} />
          </Campo>
          <Campo label="Prazo Entrega" width={125}>
            <input type="date" value={prazoEntregaItem} onChange={e => setPrazoEntregaItem(e.target.value)} />
          </Campo>
        </Secao>

        <Secao titulo="Valor e Cotação">
          <Campo label="Valor" width={110}>
            <input type="text" value={valorItem} readOnly disabled title="Calculado: (Quantidade × Preço) − Val. Desconto + Valor IPI"
              style={{ color: '#15803d', fontWeight: 700, fontSize: '0.95rem' }} />
          </Campo>
          <Campo label="Cotação" width={90}>
            <input type="text" value={cotacao} readOnly disabled title="Vem de uma busca de cotação de preço, não é digitado" />
          </Campo>
          <Campo label="% de IPI" width={90}>
            <input type="text" inputMode="decimal" value={ipiPerc} onChange={e => setIpiPerc(e.target.value)} />
          </Campo>
          <Campo label="Valor IPI" width={100}>
            <input type="text" value={ipiValor} readOnly disabled title="Calculado: (Subtotal − Val. Desconto) × % IPI" />
          </Campo>
        </Secao>

        <Secao titulo="Destino e Veículo">
          <CampoBusca label="Dest. Compra" width={90}>
            <LookupSelect
              endpoint="/erp/oedcad/lista?tipos=3,5"
              codKey="oed_cod" descKey="oed_des"
              renderItem={(item: any) => (
                <>
                  <div className="nat-item-linha1"><span className="nat-item-cod">{item.oed_cod}</span></div>
                  <div className="nat-item-dsc">{item.oed_des}</div>
                </>
              )}
              codValue={destCompraCod} descValue={destCompraCod}
              onSelect={(cod, _des, item) => {
                setDestCompraCod(cod); setDestCompraDes(item?.oed_des ?? '')
                setDestVeiObrigatorio(item?.oed_vei === 'S')
              }}
              placeholder="Buscar..." listWidth={260}
            />
          </CampoBusca>
          <Campo label=" " width={220}>
            <input type="text" value={destCompraDes} readOnly disabled />
          </Campo>
          <CampoBusca label={`Veículo${destVeiObrigatorio ? ' *' : ''}`} width={100}>
            <LookupSelect
              endpoint="/erp/veiculos"
              codKey="veicod" descKey="veipla"
              codValue={veiculoCod} descValue={veiculoCod}
              onSelect={(cod, _des, item) => { setVeiculoCod(cod); setVeiculoDes(item?.veipla ?? '') }}
              placeholder="Buscar..." listWidth={220}
            />
          </CampoBusca>
          <Campo label=" " width={200}>
            <input type="text" value={veiculoDes} readOnly disabled placeholder="Placa" />
          </Campo>
          {destVeiObrigatorio && !veiculoCod && (
            <span style={{ fontSize: '0.78rem', color: '#dc2626' }}>Este destino exige veículo informado.</span>
          )}
        </Secao>

        <Secao titulo="Situação">
          <Campo label="Situação" width={90}>
            <select value="" disabled>
              {Object.entries(SITUACAO_ITEM_LABELS).map(([cod, texto]) => (
                <option key={cod || 'aberto'} value={cod}>{texto}</option>
              ))}
            </select>
          </Campo>
          <span style={{
            fontSize: '0.75rem', fontWeight: 700, padding: '0.35rem 0.7rem', borderRadius: 6, marginBottom: 2,
            background: SITUACAO_ITEM_CORES[''].fundo, color: SITUACAO_ITEM_CORES[''].cor,
          }}>
            {SITUACAO_ITEM_LABELS['']}
          </span>
          <Campo label="Status" width={70}>
            <input type="text" value="L" readOnly disabled />
          </Campo>
        </Secao>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
          <button className="btn btn-primary" onClick={adicionarItem}
            disabled={!itemCod || !quantidadeItem || (destVeiObrigatorio && !veiculoCod)}>
            {itensOrdem.some(it => it.linha === linhaAtual) ? 'Salvar Linha ' + linhaAtual : '+ Adicionar Item'}
          </button>
        </div>

        <div style={{ overflowX: 'auto', marginBottom: '1rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#1a6ea8' }}>
                <th style={thItens}>Linha</th>
                <th style={{ ...thItens, textAlign: 'left' }}>Item</th>
                <th style={{ ...thItens, textAlign: 'right' }}>Quantidade</th>
                <th style={thItens}>Unidade</th>
                <th style={{ ...thItens, textAlign: 'right' }}>Preço Unit.</th>
                <th style={{ ...thItens, textAlign: 'right' }}>Valor</th>
                <th style={thItens}>Prazo Entrega</th>
                <th style={{ ...thItens, width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {itensOrdem.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '1.2rem', textAlign: 'center', color: '#9ca3af' }}>Nenhum item adicionado ainda.</td></tr>
              )}
              {itensOrdem.map((it, i) => (
                <tr key={it.linha} style={{ background: i % 2 ? '#f7faff' : '#fff', borderBottom: '1px solid #eef0f4' }}>
                  <td style={{ ...tdItens, textAlign: 'center', fontWeight: 700, color: '#1a6ea8' }}>{it.linha}</td>
                  <td style={tdItens}>
                    <span style={{ color: '#1a6ea8', fontWeight: 700 }}>{it.itemCod}</span> — {it.itemDes}
                  </td>
                  <td style={{ ...tdItens, textAlign: 'right', fontWeight: 700, color: '#92400e' }}>{fmtQtd(it.quantidade)}</td>
                  <td style={{ ...tdItens, textAlign: 'center' }}>{it.unidade}</td>
                  <td style={{ ...tdItens, textAlign: 'right' }}>{fmtMoeda(it.precoUnitario)}</td>
                  <td style={{ ...tdItens, textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtMoeda(it.valor)}</td>
                  <td style={{ ...tdItens, textAlign: 'center', whiteSpace: 'nowrap' }}>{fmtDataBr(it.prazoEntrega)}</td>
                  <td style={{ ...tdItens, textAlign: 'center' }}>
                    <button onClick={() => removerItem(it.linha)}
                      style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 4, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '1rem' }}>
          <div style={{
            background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 8,
            padding: '0.5rem 0.9rem', display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Quantidade Total</span>
            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#92400e' }}>{quantTotal.toLocaleString('pt-BR', { minimumFractionDigits: 3 })}</span>
          </div>
          <div style={{
            background: '#f0fdf4', border: '1px solid #dcfce7', borderRadius: 8,
            padding: '0.5rem 0.9rem', display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Valor Total</span>
            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#15803d' }}>R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {erroSalvar && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erroSalvar}</div>}
        {ordemSalva && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #86efac', color: '#15803d',
            borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem',
          }}>
            ✓ Ordem de Compra nº {numeroInput} salva com sucesso.
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => setEtapa('capa')}>
            ← Voltar
          </button>
          <button className="btn btn-primary" onClick={salvarOrdem}
            disabled={salvando || fornecedorBloqueado || itensOrdem.length === 0}>
            {salvando ? 'Salvando...' : 'Salvar Ordem de Compra'}
          </button>
          {ordemSalva && (
            <button className="btn btn-sm" onClick={() => window.open(`/api/erp/ordem-compra/${empresaCod}/${numeroInput}/comprovante`, '_blank')}
              style={{ background: '#dc2626', color: '#fff' }}>
              Imprimir Comprovante
            </button>
          )}
        </div>
      </div>
      )}

      {modalEntregaAberto && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModalEntregaAberto(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{
              margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: '#1a6ea8',
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            }}>
              📍 Dados de Entrega
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TORDENT</span>
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem 0.8rem', alignItems: 'flex-end' }}>
              <Campo label="Endereço" width={'100%'}>
                <input type="text" maxLength={40} value={enderecoEntrega} onChange={e => setEnderecoEntrega(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </Campo>
              <Campo label="CEP" width={110}>
                <input type="text" inputMode="numeric" maxLength={9} value={cepEntrega} onChange={e => setCepEntrega(e.target.value.replace(/\D/g, ''))} />
              </Campo>
              <Campo label="Cidade" width={190}>
                <input type="text" maxLength={20} value={cidadeEntrega} onChange={e => setCidadeEntrega(e.target.value)} />
              </Campo>
              <Campo label="UF" width={70}>
                <select value={ufEntrega} onChange={e => setUfEntrega(e.target.value)}>
                  <option value="">-</option>
                  {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </Campo>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.2rem' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setModalEntregaAberto(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {modalComentarioAberto && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModalComentarioAberto(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 560, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{
              margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: '#1a6ea8',
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            }}>
              💬 Comentários da Ordem de Compra
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TORDCOa</span>
            </h3>
            <textarea
              maxLength={1000}
              rows={10}
              value={comentario}
              onChange={e => setComentario(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4,
                padding: '0.5rem 0.7rem', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical',
              }}
            />
            <div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>
              {comentario.length}/1000
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.8rem' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setModalComentarioAberto(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {modalPagamentoAberto && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModalPagamentoAberto(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{
              margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: '#1a6ea8',
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            }}>
              💰 Vencimentos da Ordem de Compra
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TORDVEN</span>
            </h3>
            <p style={{ margin: '0 0 0.8rem', fontSize: '0.76rem', color: '#6b7280' }}>
              Só pra referência/impressão — não gera financeiro.
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '4px 6px', textAlign: 'left' }}>Par.</th>
                  <th style={{ padding: '4px 6px', textAlign: 'left' }}>Data Vencto</th>
                  <th style={{ padding: '4px 6px', textAlign: 'right' }}>Valor</th>
                  <th style={{ padding: '4px 6px', width: 28 }}></th>
                </tr>
              </thead>
              <tbody>
                {parcelas.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: '10px 6px', color: '#9ca3af' }}>Nenhuma parcela adicionada.</td></tr>
                )}
                {parcelas.map(p => (
                  <tr key={p.parcela} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '4px 6px', fontWeight: 700, color: '#1a6ea8' }}>{p.parcela}</td>
                    <td style={{ padding: '4px 6px' }}>
                      <input type="date" value={p.data} onChange={e => alterarParcela(p.parcela, 'data', e.target.value)} />
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                      <input type="text" inputMode="decimal" value={p.valor} onChange={e => alterarParcela(p.parcela, 'valor', e.target.value)}
                        style={{ textAlign: 'right', width: 90, color: '#15803d', fontWeight: 700 }} />
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <button onClick={() => removerParcela(p.parcela)}
                        style={{ fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.8rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={adicionarParcela}>+ Adicionar Parcela</button>
              <button className="btn btn-primary btn-sm" onClick={() => setModalPagamentoAberto(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
